import React, { useContext, useEffect, useState } from "react";
import { MachinistTableColumns } from "../../utils/tables/columns";
import { Button, Card, DatePicker, Row, Select, Table } from "antd";
import { ReportMachinistProps } from "../../interfaces/interfaces";
import DataContext from "../../context/DataContext";
import { ColumnComponent } from "../../components/ui";
import { ExportOutlined } from "@ant-design/icons";
import type { TableProps } from "antd/es/table";
import dayjs from "dayjs";
import exportFromJSON from "export-from-json";

const { RangePicker } = DatePicker;

export const ReportPage = () => {
  const [filters, setFilters] = useState({
    machinist: null,
    startDate: null,
    endDate: null,
  });
  const {
    isLoading,
    fetchOrderLineReport,
    userList,
    machinistReportData,
    fetchUserList,
  } = useContext(DataContext);
  const [exportData, setExportData] = useState<ReportMachinistProps[]>([]);

  useEffect(() => {
    fetchOrderLineReport({
      machinist: null,
      startDate: null,
      endDate: null,
    });
  }, []);

  useEffect(() => {
    if (userList.length === 0) fetchUserList();
  }, [userList]);

  const onChange: TableProps<ReportMachinistProps>["onChange"] = (
    _,
    filters,
    sorter,
    extra,
  ) => {
    setExportData(extra.currentDataSource);
  };

  const handleChange = (value: string[], key: string) => {
    let query = value.length > 0 ? value : null;
    let newFilters = { ...filters, [key]: query };
    fetchOrderLineReport(newFilters);
    setFilters(newFilters);
  };

  const button = {
    title: "Export",
    icon: <ExportOutlined />,
    action: () => {
      exportMachinistReport(machinistReportData);
    },
  };

  const exportMachinistReport = (reportData: ReportMachinistProps[]) => {
    const flattenedData = reportData.flatMap(({ Date, Data }) => {
      let totalByDate = 0;

      // Compute total by date
      Data.forEach((machinist) => {
        totalByDate += machinist.QualityLines.length;
      });

      return Data.map((machinist) => ({
        Date, // Grouping Date
        Machinist: machinist.User.Name, // Machinist Name
        TotalByMachinist: machinist.QualityLines.length, // Total per Machinist
        TotalByDate: totalByDate, // Total for that Date
      }));
    });

    // Set up file export
    const fileName = "Machinist_Report";
    const exportType = "csv"; // Options: 'csv', 'json', 'xls'

    exportFromJSON({ data: flattenedData, fileName, exportType });
  };

  return (
    <>
      <Row gutter={[24, 0]}>
        <ColumnComponent>
          <Card
            bordered={false}
            size="small"
            title={"Report"}
            styles={{
              body: { display: "none" },
            }}
            actions={[
              <></>,
              <RangePicker
                onCalendarChange={(dates) => {
                  let startDate = dates[0]
                    ? dates[0].format("YYYY-MM-DD")
                    : null;
                  let endDate = dates[1] ? dates[1].format("YYYY-MM-DD") : null;
                  let newFilters = { ...filters, startDate, endDate };
                  setFilters(newFilters as any);
                  fetchOrderLineReport(newFilters);
                }}
              />,
              <Select
                mode="multiple"
                style={{ width: "80%" }}
                placeholder="Filter by Machinist"
                onChange={(val) => handleChange(val, "machinist")}
                options={userList.map((user) => ({
                  label: user.Name,
                  value: user.OID.toString(),
                }))}
              />,
            ]}
            extra={[
              <Button type="link" title={button.title} onClick={button.action}>
                Export
              </Button>,
            ]}
          ></Card>
        </ColumnComponent>
        <ColumnComponent>
          <Card bordered hoverable className="card overflow_x">
            <Table
              columns={MachinistTableColumns}
              dataSource={machinistReportData}
              size="small"
              bordered
              loading={isLoading}
              rowKey={"OID"}
              onChange={onChange}
            />
          </Card>
        </ColumnComponent>
      </Row>
    </>
  );
};
